import { Form, Formik } from "formik"
import * as Yup from "yup"
import { Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"
import { FormikSubmitButton, FormikTextField } from "../../../../../../../shared/components/forms/Formik"
import { LabeledField } from "../../../../../../../shared/components/forms/LabeledField"

const useStyles = makeStyles()((theme) => ({
    content: {
        padding: "0 1.5rem 1.5rem",
    },
    hint: {
        color: theme.palette.tailwind.gray[500],
    },
    infoBanner: {
        padding: ".625rem .75rem",
        borderRadius: ".5rem",
        background: theme.palette.tailwind.blue[50],
        color: theme.palette.tailwind.blue[700],
    },
    infoIcon: {
        marginTop: ".125rem",
        flexShrink: 0,
    },
}))

type ThemeNameFormValues = Readonly<{
    name: string
}>

type Props = Readonly<{
    open: boolean
    isNew: boolean
    dayName: string
    /** Started dishes on the day, shown in the create-mode warning. */
    filledCount: number
    initialName: string
    onSave: (name: string) => void
    onClose: () => void
}>

export function ThemeNameDialog({ open, isNew, dayName, filledCount, initialName, onSave, onClose }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const validationSchema = Yup.object({
        name: Yup.string().trim().required(l10n.getString("merchant-menu-templates-validation-required")),
    })

    return (
        <LocalizedStrict
            id={isNew ? "merchant-week-menu-theme-dialog-add-title" : "merchant-week-menu-theme-dialog-rename-title"}
            vars={{ day: dayName }}
            attrs={{ dialogTitle: true }}
        >
            <StandardDialog open={open} dialogTitle="Theme day" onClose={onClose}>
                <Formik<ThemeNameFormValues>
                    initialValues={{ name: initialName }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => onSave(values.name.trim())}
                >
                    <Form>
                        <Stack spacing="1rem" className={classes.content}>
                            <LocalizedStrict id="merchant-week-menu-theme-dialog-hint">
                                <Typography variant="body2" className={classes.hint}>
                                    Give the theme a short name like "Spanish day" or "Christmas".
                                </Typography>
                            </LocalizedStrict>
                            <LabeledField
                                label={
                                    <LocalizedStrict id="merchant-week-menu-theme-dialog-name-label">
                                        <span>Theme name</span>
                                    </LocalizedStrict>
                                }
                            >
                                <LocalizedStrict
                                    id="merchant-week-menu-theme-dialog-name-field"
                                    attrs={{ placeholder: true }}
                                >
                                    <FormikTextField<ThemeNameFormValues>
                                        name="name"
                                        placeholder="Write theme..."
                                        fullWidth
                                    />
                                </LocalizedStrict>
                            </LabeledField>
                            {isNew && filledCount > 0 && (
                                <Stack direction="row" spacing=".5rem" className={classes.infoBanner}>
                                    <FontAwesomeIcon icon={faCircleInfo} className={classes.infoIcon} />
                                    <LocalizedStrict
                                        id="merchant-week-menu-theme-dialog-warning"
                                        vars={{ count: filledCount, day: dayName }}
                                    >
                                        <Typography variant="caption">
                                            This day has filled cells from the template. Switching to a theme day will
                                            discard them.
                                        </Typography>
                                    </LocalizedStrict>
                                </Stack>
                            )}
                            <Stack direction="row" spacing="1rem">
                                <LocalizedStrict id="merchant-week-menu-theme-dialog-close-button">
                                    <StandardButton variant="outlined" color="black" onClick={onClose}>
                                        Close
                                    </StandardButton>
                                </LocalizedStrict>
                                <LocalizedStrict
                                    id={
                                        isNew
                                            ? "merchant-week-menu-theme-dialog-create-button"
                                            : "merchant-week-menu-theme-dialog-save-button"
                                    }
                                >
                                    <FormikSubmitButton>Save</FormikSubmitButton>
                                </LocalizedStrict>
                            </Stack>
                        </Stack>
                    </Form>
                </Formik>
            </StandardDialog>
        </LocalizedStrict>
    )
}
